"use client";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Icon from "../../common/components/Icon";
import * as matomo from "../../analytics/utils/matomo";
import Obfuscate from "../../common/components/Obfuscate";
import { useTranslations } from "next-intl";
import Modal from "../../theme/components/Modal";
import CallBackForm from "./CallBackForm";

const ContactBar = () => {
  const t = useTranslations();
  const contactBarRef = useRef<HTMLDivElement>(null);
  const [isCallBackModalOpen, setIsCallBackModalOpen] = useState(false);

  const clickEventTracker = useCallback((action: string) => {
    return () => {
      matomo.trackEvent("Contact Bar Click", action, document.location.href);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const contactBar = contactBarRef.current;
    if (!contactBar) return;

    let isActive = false;
    let debounceTimer: NodeJS.Timeout;

    const checkOverlap = () => {
      const contactBarRect = contactBar.getBoundingClientRect();
      const contactBarTop = contactBarRect.top;
      const contactBarBottom = contactBarRect.bottom;

      // Check if any full-width or footer element is in the same vertical space
      const fullWidthElements = document.querySelectorAll(
        ".full-width, .footer",
      );
      let shouldActivate = false;

      for (const element of fullWidthElements) {
        const elementRect = element.getBoundingClientRect();

        // Simple vertical overlap check - no buffer
        if (
          elementRect.bottom > contactBarTop &&
          elementRect.top < contactBarBottom
        ) {
          shouldActivate = true;
          break;
        }
      }

      // Only update if state changed
      if (shouldActivate !== isActive) {
        isActive = shouldActivate;

        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          if (isActive) {
            document.body.classList.add("full-width-active");
          } else {
            document.body.classList.remove("full-width-active");
          }
        }, 50);
      }
    };

    // Throttled scroll handler
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          checkOverlap();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", checkOverlap, { passive: true });

    // Initial check with delay
    setTimeout(checkOverlap, 100);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", checkOverlap);
      clearTimeout(debounceTimer);
    };
  }, []);

  return (
    <div ref={contactBarRef} className="contact-bar">
      <Obfuscate
        className="contact-bar__item"
        onClick={clickEventTracker("call_us_button_click")}
        href="tel:+41564841500"
        aria-label={t("call_us")}
      >
        <div>
          <span>
            {t("call_us")}{" "}
            <Obfuscate element="span">{t("contact_us_number")}</Obfuscate>
          </span>
        </div>
        <Icon icon="phone" />
      </Obfuscate>
      <Obfuscate
        className="contact-bar__item"
        href="mailto:info@publicare.ch"
        onClick={clickEventTracker("email_button_click")}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t("email_us")}
      >
        <div>
          <span>{t("email_us")}</span>
        </div>
        <Icon icon="letter" fill="#ffffff" />
      </Obfuscate>
      <button
        type="button"
        className="no-button contact-bar__item align-items-center"
        onClick={() => {
          clickEventTracker("callback_button_click")();
          setIsCallBackModalOpen(true);
        }}
        aria-label={t("callback_us")}
      >
        <div>
          <span>{t("callback_us")}</span>
        </div>
        <Icon icon="phone-time" />
      </button>

      <Modal
        open={isCallBackModalOpen}
        onClose={() => setIsCallBackModalOpen(false)}
        title=""
      >
        <CallBackForm
          visible={isCallBackModalOpen}
          onHide={() => setIsCallBackModalOpen(false)}
        />
      </Modal>
    </div>
  );
};
export default ContactBar;
